import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Row, Select, Spin, Statistic, Table, Tag } from 'antd';
import moment from 'moment-timezone';
import { apiGet } from './utils/apiService';
import { fetchIotDeviceDataInPeriod, findAssetIdByCode, getDeviceIdFromAsset } from './utils/fuelService';
import { buildLagProfileReport } from './utils/lagProfileService';

const { Option } = Select;

const convertTableToCSV = (columns, dataSource, filename) => {
  const headers = columns.map((col) => col.title || col.key || '').filter(Boolean);
  const rows = dataSource.map((row, rowIndex) => columns.map((col) => {
    const dataIndex = col.dataIndex || col.key;
    let value = dataIndex ? row[dataIndex] : '';

    if (col.render && typeof col.render === 'function') {
      try {
        const rendered = col.render(value, row, rowIndex);
        if (typeof rendered === 'string' || typeof rendered === 'number') {
          value = rendered;
        } else if (rendered && rendered.props && rendered.props.children) {
          const children = rendered.props.children;
          value = Array.isArray(children) ? children.join(' ') : children;
        }
      } catch (error) {
        value = dataIndex ? row[dataIndex] : '';
      }
    }

    if (value === null || value === undefined) return '';
    const output = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (output.includes(',') || output.includes('"') || output.includes('\n')) {
      return `"${output.replace(/"/g, '""')}"`;
    }
    return output;
  }));

  const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename || 'asset_lag_profile'}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatSeconds = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  const numeric = Number(value);
  if (numeric >= 3600) return `${numeric.toFixed(2)} sec (${(numeric / 3600).toFixed(2)} hr)`;
  if (numeric >= 60) return `${numeric.toFixed(2)} sec (${(numeric / 60).toFixed(2)} min)`;
  return `${numeric.toFixed(2)} sec`;
};

const formatCount = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  return Number(value).toLocaleString('en-IN');
};

const formatPercent = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  return `${Number(value).toFixed(2)}%`;
};

const METRIC_CARDS = [
  { key: 'record_count', label: 'Valid Records', type: 'count' },
  { key: 'p50_lag', label: 'Median Lag (P50)', type: 'seconds' },
  { key: 'p95_lag', label: 'P95 Lag', type: 'seconds' },
  { key: 'max_lag', label: 'Max Lag', type: 'seconds' }
];

const SUMMARY_ROWS = [
  { key: 'min_lag', label: 'Minimum Lag', type: 'seconds' },
  { key: 'mean_lag', label: 'Average Lag', type: 'seconds' },
  { key: 'p50_lag', label: 'P50 Lag', type: 'seconds' },
  { key: 'p75_lag', label: 'P75 Lag', type: 'seconds' },
  { key: 'p90_lag', label: 'P90 Lag', type: 'seconds' },
  { key: 'p95_lag', label: 'P95 Lag', type: 'seconds' },
  { key: 'p99_lag', label: 'P99 Lag', type: 'seconds' },
  { key: 'max_lag', label: 'Maximum Lag', type: 'seconds' },
  { key: 'pct_prompt_le_5s', label: 'Prompt <= 5 sec', type: 'percent' },
  { key: 'pct_near_rt_5s_2m', label: 'Near RT 5 sec to 2 min', type: 'percent' },
  { key: 'pct_delay_2m_30m', label: 'Delayed Sync 2 min to 30 min', type: 'percent' },
  { key: 'pct_heavy_gt_30m', label: 'Heavy Delay > 30 min', type: 'percent' },
  { key: 'pct_gt_10m', label: 'Lag > 10 min', type: 'percent' },
  { key: 'pct_gt_30m', label: 'Lag > 30 min', type: 'percent' },
  { key: 'pct_gt_60m', label: 'Lag > 60 min', type: 'percent' }
];

const AVAILABILITY_ROWS = [
  { key: 'expected_packet_count', label: 'Expected Packets', type: 'count' },
  { key: 'received_packet_count', label: 'Received Packets', type: 'count' },
  { key: 'inferred_missing_packet_count', label: 'Missing Packets', type: 'count' },
  { key: 'availability_pct', label: 'Availability', type: 'percent' },
  { key: 'gap_event_count', label: 'Gap Events', type: 'count' },
  { key: 'total_data_gap_seconds', label: 'Total Data Gap', type: 'seconds' },
  { key: 'average_data_gap_seconds', label: 'Average Data Gap', type: 'seconds' },
  { key: 'median_data_gap_seconds', label: 'Median Data Gap', type: 'seconds' },
  { key: 'largest_data_gap_seconds', label: 'Largest Data Gap', type: 'seconds' },
  { key: 'gap_gt_30s', label: 'Gaps > 30 sec', type: 'count' },
  { key: 'gap_gt_60s', label: 'Gaps > 60 sec', type: 'count' },
  { key: 'gap_gt_5m', label: 'Gaps > 5 min', type: 'count' }
];

const renderMetricValue = (value, type) => {
  if (type === 'seconds') return formatSeconds(value);
  if (type === 'percent') return formatPercent(value);
  if (type === 'count') return formatCount(value);
  return value ?? '-';
};

function AssetLagProfileReport() {
  const [loading, setLoading] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetCode, setAssetCode] = useState('');
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [errors, setErrors] = useState([]);
  const [assetListForSearch, setAssetListForSearch] = useState([]);
  const [assetCodeMap, setAssetCodeMap] = useState({});
  const [selectedAssetInfo, setSelectedAssetInfo] = useState(null);
  const [reportData, setReportData] = useState({
    metadata: null,
    summary: {},
    bandCounts: {},
    bandPercentages: {},
    rows: [],
    diagnostics: null,
    availability: {
      summary: {},
      rating: null,
      gaps: [],
      diagnostics: {}
    }
  });

  useEffect(() => {
    const loadAssetList = async () => {
      setLoadingAssets(true);
      try {
        const result = await apiGet('/asset/asset-all', {
          headers: { Accept: '*/*' }
        });

        if (!result.success) {
          setErrors([`Failed to load asset list: ${result.error}`]);
          return;
        }

        const codeMap = {};
        const assetCodes = [];

        (result.data || []).forEach((asset) => {
          const code = asset.assetCode || asset['Asset Code'] || '';
          const ownerCode = asset.ownerCode || asset['Owner Code'] || '';
          const altName = asset.altName || asset['Alt Name'] || '';

          if (code) {
            codeMap[code] = {
              assetCode: code,
              ownerCode,
              altName
            };
            assetCodes.push(code);
          }
        });

        assetCodes.sort();
        setAssetCodeMap(codeMap);
        setAssetListForSearch(assetCodes);
      } catch (error) {
        setErrors([`Failed to load asset list: ${error.message}`]);
      } finally {
        setLoadingAssets(false);
      }
    };

    loadAssetList();
  }, []);

  const summaryTableData = useMemo(() => SUMMARY_ROWS.map((metric) => ({
    key: metric.key,
    metric: metric.label,
    value: reportData.summary ? reportData.summary[metric.key] : null,
    type: metric.type
  })), [reportData.summary]);

  const availabilityTableData = useMemo(() => AVAILABILITY_ROWS.map((metric) => ({
    key: metric.key,
    metric: metric.label,
    value: reportData.availability?.summary ? reportData.availability.summary[metric.key] : null,
    type: metric.type
  })), [reportData.availability]);

  const rawTableColumns = [
    {
      title: 'Event Time (IST)',
      dataIndex: 'datetime_ist',
      key: 'datetime_ist',
      width: 170
    },
    {
      title: 'Incoming Time (IST)',
      dataIndex: 'incoming_datetime_ist',
      key: 'incoming_datetime_ist',
      width: 170
    },
    {
      title: 'Lag',
      dataIndex: 'lag_seconds',
      key: 'lag_seconds',
      width: 180,
      sorter: (a, b) => Number(a.lag_seconds || 0) - Number(b.lag_seconds || 0),
      render: (value) => formatSeconds(value)
    },
    {
      title: 'Gap From Prev',
      dataIndex: 'gap_from_prev_seconds',
      key: 'gap_from_prev_seconds',
      width: 150,
      render: (value) => formatSeconds(value)
    },
    {
      title: 'Missing After Row',
      dataIndex: 'missing_packets_after_row',
      key: 'missing_packets_after_row',
      width: 140,
      render: (value) => formatCount(value)
    },
    {
      title: 'Gap After Row',
      dataIndex: 'gap_after_row_seconds',
      key: 'gap_after_row_seconds',
      width: 150,
      render: (value) => formatSeconds(value)
    },
    {
      title: 'Device ID',
      dataIndex: 'device_id',
      key: 'device_id',
      width: 160,
      render: (value) => value || '-'
    }
  ];

  const rawExportColumns = [
    { title: 'Event Time (IST)', dataIndex: 'datetime_ist', key: 'datetime_ist' },
    { title: 'Incoming Time (IST)', dataIndex: 'incoming_datetime_ist', key: 'incoming_datetime_ist' },
    { title: 'Event Timestamp', dataIndex: 'timestamp', key: 'timestamp' },
    { title: 'Incoming Timestamp', dataIndex: 'incoming_time', key: 'incoming_time' },
    { title: 'Lag Seconds', dataIndex: 'lag_seconds', key: 'lag_seconds' },
    { title: 'Seconds From Window Start', dataIndex: 'seconds_from_window_start', key: 'seconds_from_window_start' },
    { title: 'Slot Index', dataIndex: 'slot_index', key: 'slot_index' },
    { title: 'Gap From Prev Seconds', dataIndex: 'gap_from_prev_seconds', key: 'gap_from_prev_seconds' },
    { title: 'Inferred Missing From Prev', dataIndex: 'inferred_missing_from_prev', key: 'inferred_missing_from_prev' },
    { title: 'Missing Packets After Row', dataIndex: 'missing_packets_after_row', key: 'missing_packets_after_row' },
    { title: 'Gap After Row Seconds', dataIndex: 'gap_after_row_seconds', key: 'gap_after_row_seconds' },
    { title: 'Primary Slot Sample', dataIndex: 'is_primary_slot_sample', key: 'is_primary_slot_sample' },
    { title: 'Device ID', dataIndex: 'device_id', key: 'device_id' },
    { title: 'Location', dataIndex: 'location', key: 'location' },
    { title: 'Zone', dataIndex: 'zone', key: 'zone' },
    { title: 'Lat/Long', dataIndex: 'lat_long', key: 'lat_long' }
  ];

  const gapColumns = [
    {
      title: 'Gap Start (IST)',
      dataIndex: 'gap_start_ist',
      key: 'gap_start_ist',
      width: 170
    },
    {
      title: 'Gap End (IST)',
      dataIndex: 'gap_end_ist',
      key: 'gap_end_ist',
      width: 170
    },
    {
      title: 'Duration',
      dataIndex: 'gap_duration_seconds',
      key: 'gap_duration_seconds',
      width: 160,
      sorter: (a, b) => Number(a.gap_duration_seconds || 0) - Number(b.gap_duration_seconds || 0),
      render: (value) => formatSeconds(value)
    },
    {
      title: 'Missing Packets',
      dataIndex: 'missing_packets',
      key: 'missing_packets',
      width: 130,
      render: (value) => formatCount(value)
    },
    {
      title: 'Previous Event',
      dataIndex: 'previous_event_time_ist',
      key: 'previous_event_time_ist',
      width: 170
    },
    {
      title: 'Next Event',
      dataIndex: 'next_event_time_ist',
      key: 'next_event_time_ist',
      width: 170
    }
  ];

  const summaryColumns = [
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric',
      width: 260
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value, row) => renderMetricValue(value, row.type)
    }
  ];

  const validateInputs = () => {
    if (!assetCode || !assetCode.trim()) {
      return ['Please enter an Asset Code'];
    }
    if (!startDateTime) {
      return ['Please select a day for the 24-hour profile'];
    }
    if (!endDateTime) {
      return ['Day end could not be resolved. Please reselect the day.'];
    }

    const hoursDiff = endDateTime.diff(startDateTime, 'hours', true);
    if (Math.abs(hoursDiff - 24) > 0.05) {
      return ['This report only supports one IST calendar day (00:00:00 to 23:59:59).'];
    }

    return [];
  };

  const loadData = async () => {
    const validationErrors = validateInputs();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      const trimmedAssetCode = assetCode.trim();
      const assetResult = await findAssetIdByCode(trimmedAssetCode);
      if (!assetResult.success) {
        setErrors([`Failed to find asset: ${assetResult.error}`]);
        setLoading(false);
        return;
      }

      const deviceResult = await getDeviceIdFromAsset(trimmedAssetCode, assetResult.assetId);
      if (!deviceResult.success) {
        setErrors([`Failed to get device ID: ${deviceResult.error}`]);
        setLoading(false);
        return;
      }

      const windowStart = startDateTime.clone().startOf('day');
      const windowEnd = windowStart.clone().endOf('day');
      const windowEndExclusive = windowStart.clone().add(1, 'day');
      const startTimestamp = windowStart.clone().utc().valueOf();
      const endTimestamp = windowEnd.clone().utc().valueOf();

      const result = await fetchIotDeviceDataInPeriod(
        deviceResult.deviceId,
        startTimestamp,
        endTimestamp,
        assetResult.assetId
      );

      if (!result.success) {
        setErrors([`Failed to fetch IoT data: ${result.error}`]);
        setLoading(false);
        return;
      }

      const computed = buildLagProfileReport(result.data, {
        assetId: assetResult.assetId,
        assetCode: trimmedAssetCode,
        cadenceSeconds: 10,
        windowStartMs: windowStart.valueOf(),
        windowEndMs: windowEnd.valueOf(),
        windowEndExclusiveMs: windowEndExclusive.valueOf()
      });

      setReportData(computed);
      setSelectedAssetInfo({
        assetCode: trimmedAssetCode,
        assetId: assetResult.assetId,
        deviceId: deviceResult.deviceId,
        ownerCode: assetCodeMap[trimmedAssetCode]?.ownerCode || null,
        altName: assetCodeMap[trimmedAssetCode]?.altName || null,
        windowStartIst: computed.metadata?.windowStartIst,
        windowEndIst: computed.metadata?.windowEndIst
      });
    } catch (error) {
      setErrors([error.message || 'Failed to generate lag profile']);
    } finally {
      setLoading(false);
    }
  };

  const selectedDayLabel = startDateTime ? startDateTime.format('DD MMM YYYY') : null;
  const availabilityRating = reportData.availability?.rating;

  return (
    <Card title="Asset Lag & Availability Profile Report">
      {loading && <Spin size="large" style={{ marginBottom: 16 }} />}

      {errors.length > 0 && (
        <Alert
          message="Errors"
          description={errors.map((error, index) => <div key={index}>{error}</div>)}
          type="error"
          style={{ marginBottom: 16 }}
        />
      )}

      <Card title="Data Input" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <div>
              <label>Asset Code <span style={{ color: 'red' }}>*</span>:</label>
              <Select
                showSearch
                placeholder="Search by Asset Code, Owner Code, or Alt Name"
                value={assetCode || undefined}
                onChange={(value) => {
                  setAssetCode(value || '');
                  setErrors([]);
                }}
                style={{ marginTop: 4, width: '100%' }}
                loading={loadingAssets}
                allowClear
                filterOption={(input, option) => {
                  if (!input || String(input).trim() === '') return true;
                  const searchText = String(input).toLowerCase().trim();
                  const displayText = option.children || option.props?.children || '';
                  const code = option.value || option.props?.value || '';
                  const assetInfo = assetCodeMap[code] || {};
                  return String(displayText).toLowerCase().includes(searchText) ||
                    String(code).toLowerCase().includes(searchText) ||
                    String(assetInfo.ownerCode || '').toLowerCase().includes(searchText) ||
                    String(assetInfo.altName || '').toLowerCase().includes(searchText);
                }}
              >
                {assetListForSearch.map((code) => {
                  const assetInfo = assetCodeMap[code];
                  const displayText = assetInfo
                    ? `${code}${assetInfo.ownerCode ? ` (${assetInfo.ownerCode})` : ''}${assetInfo.altName ? ` - ${assetInfo.altName}` : ''}`
                    : code;
                  return <Option key={code} value={code}>{displayText}</Option>;
                })}
              </Select>
              <small style={{ color: '#999' }}>Search by Asset Code, Owner Code, or Alt Name</small>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <label>Profile Day (IST) <span style={{ color: 'red' }}>*</span>:</label>
              <DatePicker
                format="YYYY-MM-DD"
                value={startDateTime}
                onChange={(date) => {
                  if (date) {
                    const istMoment = moment.tz(date, 'Asia/Kolkata').startOf('day');
                    setStartDateTime(istMoment);
                    setEndDateTime(istMoment.clone().endOf('day'));
                    setErrors([]);
                  } else {
                    setStartDateTime(null);
                    setEndDateTime(null);
                  }
                }}
                style={{ marginTop: 4, width: '100%' }}
                placeholder="Select analysis day"
              />
              <small style={{ color: '#999' }}>The report is locked to one IST calendar day.</small>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <label>24-Hour Window (IST):</label>
              <div
                style={{
                  marginTop: 4,
                  minHeight: 32,
                  padding: '6px 11px',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  background: '#fafafa'
                }}
              >
                {startDateTime && endDateTime
                  ? `${startDateTime.format('YYYY-MM-DD HH:mm:ss')} to ${endDateTime.format('YYYY-MM-DD HH:mm:ss')}`
                  : 'Select a day to lock the 24-hour window'}
              </div>
              <small style={{ color: '#999' }}>Start auto-locks to 00:00:00 and end auto-locks to 23:59:59.</small>
            </div>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <Button
              type="primary"
              onClick={loadData}
              loading={loading}
              disabled={!assetCode || !startDateTime || !endDateTime}
              block
            >
              Load 24-Hour Profile
            </Button>
          </Col>
          <Col span={16}>
            <small style={{ color: '#666' }}>
              Expected nominal cadence: <strong>1 packet every 10 seconds</strong>. Shorter bursts are allowed and are not penalized.
            </small>
          </Col>
        </Row>
      </Card>

      {selectedAssetInfo && (
        <Card
          title="Selected Asset"
          style={{ marginBottom: 16 }}
          extra={<Tag color="blue">{selectedAssetInfo.deviceId || 'No Device ID'}</Tag>}
        >
          <Row gutter={16}>
            <Col span={6}><strong>Asset Code:</strong> {selectedAssetInfo.assetCode}</Col>
            <Col span={6}><strong>Owner Code:</strong> {selectedAssetInfo.ownerCode || '-'}</Col>
            <Col span={6}><strong>Alt Name:</strong> {selectedAssetInfo.altName || '-'}</Col>
            <Col span={6}><strong>Profile Day:</strong> {selectedDayLabel || '-'}</Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={24}>
              <strong>Window:</strong> {selectedAssetInfo.windowStartIst || '-'} to {selectedAssetInfo.windowEndIst || '-'}
            </Col>
          </Row>
        </Card>
      )}

      {reportData.diagnostics && (
        <Card title="Lag Snapshot" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            {METRIC_CARDS.map((item) => (
              <Col span={6} key={item.key}>
                <Statistic
                  title={item.label}
                  value={reportData.summary[item.key] ?? 0}
                  formatter={(value) => renderMetricValue(value, item.type)}
                />
              </Col>
            ))}
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={6}><Tag color="green">Prompt</Tag> {formatPercent(reportData.bandPercentages.prompt)} ({formatCount(reportData.bandCounts.prompt || 0)})</Col>
            <Col span={6}><Tag color="blue">Near RT</Tag> {formatPercent(reportData.bandPercentages.near_rt)} ({formatCount(reportData.bandCounts.near_rt || 0)})</Col>
            <Col span={6}><Tag color="orange">Delayed</Tag> {formatPercent(reportData.bandPercentages.delay)} ({formatCount(reportData.bandCounts.delay || 0)})</Col>
            <Col span={6}><Tag color="red">Heavy Delay</Tag> {formatPercent(reportData.bandPercentages.heavy)} ({formatCount(reportData.bandCounts.heavy || 0)})</Col>
          </Row>
        </Card>
      )}

      {reportData.availability?.summary && reportData.diagnostics && (
        <Card
          title="Availability Snapshot"
          style={{ marginBottom: 16 }}
          extra={availabilityRating ? <Tag color={availabilityRating.color}>{availabilityRating.level.toUpperCase()}</Tag> : null}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Statistic
                title="Availability"
                value={reportData.availability.summary.availability_pct ?? 0}
                formatter={(value) => formatPercent(value)}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Missing Packets"
                value={reportData.availability.summary.inferred_missing_packet_count ?? 0}
                formatter={(value) => formatCount(value)}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Total Data Gap"
                value={reportData.availability.summary.total_data_gap_seconds ?? 0}
                formatter={(value) => formatSeconds(value)}
              />
            </Col>
            <Col span={6}>
              <Statistic
                title="Largest Data Gap"
                value={reportData.availability.summary.largest_data_gap_seconds ?? 0}
                formatter={(value) => formatSeconds(value)}
              />
            </Col>
          </Row>
          {availabilityRating && (
            <Alert
              style={{ marginTop: 16 }}
              type={availabilityRating.level === 'poor' ? 'error' : availabilityRating.level === 'degraded' ? 'warning' : 'info'}
              message={`Availability Rating: ${availabilityRating.level.toUpperCase()}`}
              description={availabilityRating.reason}
              showIcon
            />
          )}
        </Card>
      )}

      {reportData.diagnostics && (
        <Card title="Diagnostics" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}><Statistic title="Total API Rows" value={reportData.diagnostics.totalRows || 0} /></Col>
            <Col span={6}><Statistic title="Valid Rows" value={reportData.diagnostics.validRows || 0} /></Col>
            <Col span={6}><Statistic title="Skipped Rows" value={reportData.diagnostics.skippedRows || 0} /></Col>
            <Col span={6}><Statistic title="Negative Lag Rows" value={reportData.diagnostics.negativeLagRows || 0} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={6}><Statistic title="Missing Event Time" value={reportData.diagnostics.missingEventTime || 0} /></Col>
            <Col span={6}><Statistic title="Missing Incoming Time" value={reportData.diagnostics.missingIncomingTime || 0} /></Col>
            <Col span={6}><Statistic title="Duplicate/Burst Rows" value={reportData.diagnostics.duplicateSlotRows || 0} /></Col>
            <Col span={6}><Statistic title="Covered Slots" value={reportData.diagnostics.uniqueCoveredSlots || 0} /></Col>
          </Row>
        </Card>
      )}

      <Card title="Lag Metrics" style={{ marginBottom: 16 }}>
        <Table
          columns={summaryColumns}
          dataSource={summaryTableData}
          pagination={false}
          rowKey="key"
          locale={{ emptyText: 'Load telemetry to calculate lag metrics.' }}
        />
      </Card>

      <Card title="Availability Metrics" style={{ marginBottom: 16 }}>
        <Table
          columns={summaryColumns}
          dataSource={availabilityTableData}
          pagination={false}
          rowKey="key"
          locale={{ emptyText: 'Load telemetry to calculate availability metrics.' }}
        />
      </Card>

      <Card title="Detected Data Gaps" style={{ marginBottom: 16 }}>
        <Table
          columns={gapColumns}
          dataSource={reportData.availability?.gaps || []}
          rowKey="key"
          scroll={{ x: 980 }}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: loading ? 'Loading telemetry...' : 'No data gaps were inferred for the selected day.' }}
        />
      </Card>

      <Card
        title="Lag-Enriched Raw Telemetry"
        extra={(
          <Button
            onClick={() => convertTableToCSV(rawExportColumns, reportData.rows, `asset_lag_profile_${selectedAssetInfo?.assetCode || 'asset'}`)}
            disabled={!reportData.rows.length}
          >
            Export CSV
          </Button>
        )}
      >
        <Table
          columns={rawTableColumns}
          dataSource={reportData.rows}
          rowKey="key"
          scroll={{ x: 1200, y: 520 }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: loading ? 'Loading telemetry...' : 'No valid lag rows found for the selected day.' }}
        />
      </Card>
    </Card>
  );
}

export default AssetLagProfileReport;
